import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  Grid2,
  MenuItem,
  Select,
  Typography
} from '@mui/material'
import { t } from 'i18next'
import { APP_COLOR } from 'utils/colors'
import { RANGE } from 'components/calendar/CalendarHeader'
import { StyledDialog } from '../styled.dialog'
import { InviteLinkRow } from 'components/badges/InviteLinkBadge'
import {
  AccountDoc,
  GroupDoc,
  GroupStatus,
  UserSettingsDoc
} from 'types/api/changes'
import {
  CalendarRange,
  getCalendarRangeByString
} from 'types/components/calendar'
import { useCalendar } from 'components/providers/CalendarProvider'
import SizeTextFieldInput from './styled'
import GuestMode from 'components/guest/GuestMode'
import LocalStorage from 'services/LocalStorage'
import { LocalStorageKey } from 'types/services/localStorage'
import { GroupsList } from 'components/sidebar/GroupsList'
import Groups from 'components/sidebar/Groups'
import { allGroups, allGroupsForUI } from 'services/db/Queries/GroupQueries'
import GroupsPicker from './GroupsPicker'
import i18n from 'i18n'

interface EmbedCalendarDialogProps {
  group: GroupDoc
  open: boolean
  handleClose: () => void
  userSettings: UserSettingsDoc
  account: AccountDoc
}

const EmbedCalendarDialog: React.FC<EmbedCalendarDialogProps> = ({
  group,
  open,
  handleClose,
  userSettings,
  account
}) => {
  const [width, setWidth] = useState('800')
  const [height, setHeight] = useState('800')
  const [selectedValue, setSelectedValue] = useState(RANGE[2].value as string)
  const [showHeader, setShowHeader] = useState(false)
  const [selectedGroups, setSelectedGroups] = useState<GroupDoc[]>([group])
  const [calendarTitle, setCalendarTitle] = useState(group.Name)

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target

    let modified = Number(value)
    if (modified > 2000) modified = 2000
    if (name === 'input1') {
      setWidth(`${modified}`)
    } else if (name === 'input2') {
      setHeight(`${modified}`)
    } else if (name === 'calendarTitle') {
      setCalendarTitle(value)
    }
  }

  const handleSelectChange = (event: string) => {
    setSelectedValue(event)
    calendar.onView(event as CalendarRange)
  }

  const calendar = useCalendar()
  let groupLink = ``

  if (selectedGroups.length > 1) {
    const groupIds = selectedGroups.map((group) => group._id).join(',')

    groupLink = `${
      process.env.REACT_APP_LINK_DOMAIN
    }/calendars?groupIds=${groupIds}&view=${selectedValue}&showHeader=${
      showHeader ? '1' : '0'
    }&calendarTitle=${encodeURIComponent(calendarTitle)}`
  } else {
    groupLink = `${process.env.REACT_APP_LINK_DOMAIN}/calendar/${
      selectedGroups[0]._id
    }⁠${
      selectedGroups[0].PrivateLinkPassword
    }?view=${selectedValue}&showHeader=${
      showHeader ? '1' : '0'
    }&calendarTitle=${encodeURIComponent(calendarTitle)}`
  }

  const iFrameString = `<iframe src="${groupLink}" width="${width}" frameborder="0" height="${height}"></iframe>`

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowHeader(event.target.checked)
  }

  return (
    <StyledDialog
      disableEscapeKeyDown
      open={open}
      maxWidth="md"
      fullWidth
      onClose={handleClose}
      PaperProps={{
        style: {
          height: '90%',
          maxHeight: '90%',
          width: '90%',
          maxWidth: '90%',
          borderRadius: 8
        }
      }}
    >
      <DialogContent sx={{ display: 'flex', height: '100%', paddingTop: 16 }}>
        <Box
          sx={{
            maxWidth: '333px',
            paddingTop: '1rem',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            marginRight: i18n.dir() === 'ltr' ? '16px' : undefined,
            marginLeft: i18n.dir() === 'rtl' ? '16px' : undefined
          }}
          gap={4}
        >
          <SizeTextFieldInput
            name="input1"
            value={width}
            label={`${t('width')}`}
            onChange={handleInputChange}
          />
          <SizeTextFieldInput
            label={`${t('height')}`}
            name="input2"
            value={height}
            onChange={handleInputChange}
          />
          <div>
            <FormControl fullWidth sx={{ position: 'relative' }}>
              <Typography
                position={'absolute'}
                fontSize={'0.75rem'}
                color={'#848484'}
                sx={{
                  marginLeft: i18n.dir() === 'ltr' ? 2 : undefined,
                  marginRight: i18n.dir() === 'rtl' ? 2 : undefined,
                  paddingLeft: '4px',
                  paddingRight: '4px',
                  marginTop: -2,
                  backgroundColor: 'white',
                  background: 'white',
                  zIndex: 10000
                }}
              >
                {t('defaultView')}
              </Typography>
              <Select
                value={selectedValue}
                sx={{ zIndex: 9999 }}
                onChange={(event) => {
                  handleSelectChange(event.target.value)
                }}
                onOpen={() => {
                  LocalStorage.set(LocalStorageKey.SHOULD_KEEP_POPUP, '1')
                }}
                onClose={() => {
                  LocalStorage.set(LocalStorageKey.SHOULD_KEEP_POPUP, '0')
                }}
                MenuProps={{
                  style: {
                    zIndex: 9999
                  }
                }}
              >
                {RANGE.map((item) => (
                  <MenuItem
                    key={item.value?.toString()}
                    autoFocus={false}
                    value={item.value as string}
                  >
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ marginTop: 4 }} fullWidth>
              <SizeTextFieldInput
                type="text"
                name="calendarTitle"
                maxLength={25}
                value={calendarTitle}
                label={t('calendarName')}
                onChange={handleInputChange}
              />
            </FormControl>
          </div>
          <FormControlLabel
            control={
              <Checkbox checked={showHeader} onChange={handleCheckboxChange} />
            }
            label={t('showHeader')}
          />
          <GroupsPicker
            userSettings={userSettings}
            account={account}
            selectedGroups={selectedGroups}
            openedFromGroup={group}
            onGroupToggle={(group) => {
              if (selectedGroups.find((g) => g._id === group._id)) {
                setSelectedGroups(
                  selectedGroups.filter((g) => g._id !== group._id)
                )
              } else {
                setSelectedGroups([...selectedGroups, group])
              }
            }}
          />
        </Box>
        <div style={{ flex: 1, width: '50%', maxWidth: '800px' }}>
          <Box
            sx={{ width: '100%' }}
            display={'flex'}
            flexDirection={'column'}
            paddingLeft={4}
            paddingRight={4}
            gap={4}
          >
            <Typography>{t('toDisplayCalendar')}</Typography>
            <InviteLinkRow textSize="16px" link={iFrameString} />
          </Box>
          <Box
            sx={{
              pointerEvents: 'none'
            }}
            minWidth={'100px'}
            minHeight={'100px'}
            height={`${height}px`}
            width={`${width}px`}
          >
            <GuestMode
              preview
              showHeader={showHeader ? '1' : '0'}
              defaultView={getCalendarRangeByString(selectedValue)}
              groupId={selectedGroups[0]._id ?? group._id ?? ''}
              groupsIds={selectedGroups.map((group) => group._id ?? '')}
              height={`${height ?? 800}px`}
              width={`${width ?? 800}px`}
              calendarTitle={calendarTitle}
            />
          </Box>
        </div>
      </DialogContent>
      <DialogActions>
        <Button sx={{ color: APP_COLOR }} onClick={handleClose}>{`${t(
          'close'
        )}`}</Button>
      </DialogActions>
    </StyledDialog>
  )
}

export default EmbedCalendarDialog
